import { Suspense } from "react";
import Container from "~/components/atoms/Container";
import { Skeleton } from "~/components/ui/skeleton";
import { lazy } from "react";
import BlogHeroBanner from "../HeroBanners/BlogHeroBanner";
import { BlogCategoryTextHero } from "../HeroBanners/BlogCategoryTextHero";
import { getBlogChipMarketingHeroCopy } from "~/lib/constants/blogChipMarketingHero";
import ButtonSearchbar from "../Searchbars/ButtonSearchbar";
const Breadcrumbs = lazy(() => import("~/components/blocks/BreadCrumbs"));
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
} from "~/components/ui/custom-radix-components/pagination";
import CustomPagination from "~/components/blocks/Pagination/Pagination";
import BlogCategoriesSection from "../Sections/BlogCategoriesSection";
import SubscribeBanner from "../SubscribeBanner/SubscribeBanner";
import BlogsFilterChips from "../Chips/BlogsFilterChips";
import NoResults from "../Card/NoResultCard";
import { buildBlogChipTopics } from "~/lib/constants/blogFilterChips";

interface DesktopLayoutProps {
    title: string;
    blogData: any;
    renderBlogs: () => JSX.Element | JSX.Element[];
    handlePageChange: (page: number) => void;
    pageInfo: any;
    params: any;
    featuredBlogs: any;
    onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
    currentPage: number;
    showFilterChips: boolean;
    selected: string[];
    toggleTopic: (topic: string) => void;
    noResults?: boolean;
    clearFilters: () => void;
    search: string;
    country: string;
    /** Text-only category hero (news and other listing pages) */
    marketingHero?: boolean;
    marketingHeroCopyKey?: string;
    selectedChip?: string;
}

export default function DesktopLayout({ title, blogData, renderBlogs, handlePageChange, pageInfo, params, featuredBlogs, onSearch, currentPage, showFilterChips, selected, toggleTopic, noResults = false, clearFilters, search, country, marketingHero = false, marketingHeroCopyKey, selectedChip }: DesktopLayoutProps) {
    const hasBlogs = blogData?.blogs && blogData.blogs.length > 0;
    const chipTopics = buildBlogChipTopics(country);
    const heroKey = marketingHeroCopyKey ?? selectedChip;
    const marketingCopy = marketingHero && heroKey ? getBlogChipMarketingHeroCopy(heroKey) : null;

    return (
        <>
            <Container htmlFor="ultra-wide">
                <Suspense fallback={<Skeleton className="h-6 w-32" />}>
                    <Breadcrumbs params={params} className="hidden md:block mt-4" />
                </Suspense>

                {marketingCopy ? (
                    <BlogCategoryTextHero
                        title={title}
                        description={marketingCopy.mobileDescription}
                    />
                ) : featuredBlogs?.blogs?.[0] ? (
                    <BlogHeroBanner
                        mainArticle={featuredBlogs?.blogs?.[0]}
                        isFeaturedAvailable={true}
                        featuredPosts={featuredBlogs?.blogs?.slice(1)}
                        country={country}
                    />
                ) : null}

                {(hasBlogs || noResults) && (
                    <div className="w-full px-4 py-6 bg-oh-icyGray rounded-lg">
                        <ButtonSearchbar title={title} btnText={"Search"} onSearch={onSearch} value={search} />

                        {showFilterChips && (
                            <BlogsFilterChips toggleTopic={toggleTopic} selected={selected} topics={chipTopics} />
                        )}

                        {hasBlogs && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 my-4">
                                {renderBlogs()}
                            </div>
                        )}

                        {noResults && (
                            <div className="grid grid-cols-1 my-4">
                                <NoResults onClear={() => clearFilters()} />
                            </div>
                        )}

                        {hasBlogs && pageInfo?.totalPages > 1 && (
                            <div className="flex justify-center my-4">
                                <Pagination className="max-w-[550px] w-full justify-center">
                                    <PaginationContent className="flex items-center">
                                        {currentPage > 1 ? (
                                            <PaginationItem className="cursor-pointer">
                                                <PaginationPrevious
                                                    onClick={() =>
                                                        handlePageChange(currentPage - 1)
                                                    }
                                                />
                                            </PaginationItem>
                                        ) : (
                                            <div className="w-[60px]" />
                                        )}

                                        <CustomPagination
                                            currentPage={currentPage}
                                            handlePageChange={handlePageChange}
                                            totalPages={pageInfo?.totalPages}
                                        />

                                        {currentPage < pageInfo?.totalPages ? (
                                            <PaginationItem className="cursor-pointer">
                                                <PaginationNext
                                                    onClick={() =>
                                                        handlePageChange(currentPage + 1)
                                                    }
                                                />
                                            </PaginationItem>
                                        ) : (
                                            <div className="w-[40px]" />
                                        )}
                                    </PaginationContent>
                                </Pagination>
                            </div>
                        )}
                    </div>
                )}

                <BlogCategoriesSection />
            </Container>
            <SubscribeBanner />
        </>
    );
}
